import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import {
  fetchViews, createView, updateView, deleteView,
  type EventView,
} from '../event-manager-client';

export function useEventViews(campaignId: string) {
  const { apiFetch } = useApi();

  const query = useQuery<EventView[]>({
    queryKey: ['launcher', 'campaigns', campaignId, 'views'],
    queryFn: () => fetchViews(apiFetch, campaignId),
    enabled: !!campaignId,
    staleTime: Infinity,
  });

  return {
    views: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}

export function useCreateView(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; columns: string[]; column_widths?: Record<string, number> }) =>
      createView(apiFetch, campaignId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'views'] });
    },
  });
}

export function useUpdateView(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ viewId, fields }: { viewId: number; fields: { name?: string; columns?: string[]; column_widths?: Record<string, number> } }) =>
      updateView(apiFetch, campaignId, viewId, fields),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'views'] });
    },
  });
}

export function useDeleteView(campaignId: string) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (viewId: number) => deleteView(apiFetch, campaignId, viewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'views'] });
    },
  });
}
